// src/contexts/NavigationBlockContext.tsx
import React, { createContext, useContext, useState } from 'react';

type NavigationBlockContextType = {
  isBlocked: boolean;
  setBlocked: (b: boolean) => void;
  requestNavigation: (path: string) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  navDialogOpen: boolean;
  pendingPath: string | null;
  registerNavigate?: (fn: (path: string) => void) => void;
};

const NavigationBlockContext = createContext<NavigationBlockContextType | undefined>(undefined);

export const NavigationBlockProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isBlocked, setBlocked] = useState(false);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [navigateFn, setNavigateFn] = useState<((path: string) => void) | null>(null);

  const registerNavigate = (fn: (path: string) => void) => setNavigateFn(() => fn);

  const requestNavigation = (path: string) => {
    setPendingPath(path);
    setNavDialogOpen(true);
  };

  const confirmNavigation = () => {
    setNavDialogOpen(false);
    setBlocked(false);
    if (pendingPath && navigateFn) {
      navigateFn(pendingPath);
      setPendingPath(null);
    }
  };

  const cancelNavigation = () => {
    setNavDialogOpen(false);
    setPendingPath(null);
  };

  return (
    <NavigationBlockContext.Provider value={{
      isBlocked, setBlocked,
      requestNavigation,
      confirmNavigation,
      cancelNavigation,
      navDialogOpen,
      pendingPath,
      registerNavigate,
    }}>
      {children}
    </NavigationBlockContext.Provider>
  );
};

export const useNavigationBlock = () => {
  const ctx = useContext(NavigationBlockContext);
  if (!ctx) throw new Error("useNavigationBlock must be used within NavigationBlockProvider");
  return ctx;
};